import { availabilityDays, DAY_SLOTS, zoneLabel } from '@concord/domain';
import {
  Button,
  ChosenTimes,
  Notice,
  type PickedSlot,
  QCard,
  Saved,
  TimePicker,
  WizardNav,
} from '@concord/ui';
import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { useSetTimes } from '../api';
import type { StepProps } from '../IntakeWizard';

const ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

/** SCH-01 to SCH-05: the day-by-slot picker, the running count, the zone on screen. */
export function TimesStep({ v, back, next, mode }: StepProps) {
  const setTimes = useSetTimes(v.id);
  const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [tz, setTz] = useState(() => v.patient.timezone || deviceTz);
  const [picked, setPicked] = useState<PickedSlot[]>(() =>
    v.times.map((t) => ({ date: t.date, time: t.time })),
  );
  const [changingTz, setChangingTz] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const days = availabilityDays(new Date(), tz, v.rules.horizonDays);

  const save = (slots: PickedSlot[], zone = tz) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setTimes.mutate({ tz: zone, slots }), 400);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const change = (slots: PickedSlot[]) => {
    setPicked(slots);
    save(slots);
  };

  const label = (time: string) => DAY_SLOTS.find((d) => d.time === time)?.label ?? time;
  const groups = days
    .map((d) => ({
      day: d.label,
      items: picked
        .filter((p) => p.date === d.date)
        .map((p) => ({ key: `${p.date} ${p.time}`, label: label(p.time), slot: p })),
    }))
    .filter((g) => g.items.length);
  const navName = mode === 'staff' ? 'you' : 'your care navigator';

  return (
    <>
      <QCard
        label="Pick times"
        title={
          mode === 'staff'
            ? 'When is the patient free in the next two weeks?'
            : 'When are you free in the next two weeks?'
        }
        lead={`Pick a day, then tap every half-hour you could start. Weekday daytime works best for physicians, but offer whatever is true${mode === 'staff' ? '' : ' for you'}.`}
      >
        <TimePicker
          days={days}
          slots={DAY_SLOTS}
          selected={picked}
          onChange={change}
          max={v.rules.maxTimes}
          zone={
            <>
              Times in <b>{zoneLabel(tz)}</b>
              {tz === deviceTz ? ', taken from your device.' : '.'}
            </>
          }
          zoneControl={
            changingTz ? (
              <select
                className="tzsel"
                aria-label="Time zone"
                value={tz}
                onChange={(e) => {
                  setTz(e.target.value);
                  setChangingTz(false);
                  save(picked, e.target.value);
                }}
              >
                {[...new Set([tz, ...ZONES])].map((z) => (
                  <option key={z} value={z}>
                    {zoneLabel(z)}
                  </option>
                ))}
              </select>
            ) : (
              <button
                type="button"
                className="link"
                style={{ fontSize: '.8rem' }}
                onClick={() => setChangingTz(true)}
              >
                Change time zone
              </button>
            )
          }
        />
        {picked.length > 0 && (
          <ChosenTimes
            groups={groups}
            onRemove={(slot) => change(picked.filter((p) => !(p.date === slot.date && p.time === slot.time)))}
            note={`At least one time is needed before ${navName === 'you' ? 'the case can go' : `${navName} can send your case`} to physicians. Each physician sees these in their own time zone. If none work for them, they can ask for another.`}
          />
        )}
        {setTimes.isError && <Notice tone="bad">{errorMessage(setTimes.error)}</Notice>}
      </QCard>
      <WizardNav
        back={
          <Button variant="sec" onClick={back}>
            Back to records
          </Button>
        }
        status={setTimes.isPending ? 'Saving…' : <Saved>Saved</Saved>}
        next={
          <Button
            size="lg"
            onClick={() => {
              if (timer.current) {
                clearTimeout(timer.current);
                setTimes.mutate({ tz, slots: picked });
              }
              next();
            }}
          >
            Continue to review
          </Button>
        }
      />
    </>
  );
}
